// Mr Holden, the bagholder (sprint 36 item 3): the first bear look. A bear knock in every rule (the server rolls 'bear': same
// odds share, x2 if wrong, normal points if right); only the visitor behind the glass, its light and its plate differ. The round
// message carries `look: 'holden'` beside `special: 'bear'`; neighbours-lit.js routes him (litOf, litPlate, litState).
//   holden : a short, stocky man in a flat cloth cap, arms held out from his coat, a knotted bag gathered in each fist and one
//            hooked on his left elbow, and four tied bags heaped on his shoulders. Every bag is its own shape, parted from the
//            man and from the next bag by a lit seam. The top of the outline is the cap's flat line and four knots spread wide
//            (never two round ears). Light: kraft brown. Reveal: the elbow bag slips down to his fist, the bag on his right
//            shoulder slumps and he hugs the fist bags (a can drops), then he looks up over the bags and the eyes tell.
// Nothing is drawn here: the figure is the mask sheet drawn in Aseprite (art/aseprite/src/holden_door.lua ->
// client/art/aseprite/holden-door/), one frame per reveal step, and its anchors (head, mouth, eye points per step).
// Sheet palette: 1 ink = the figure; 2 pale = pale cloth (detail shade 2); 3 kraft = lit from behind (detail shade 3).
import { GLASS } from './visitors.js';
import { T } from './bits.js';
import { pickEye, eyeLook, decodeIndexed } from './eyes.js';

export const FPS = 15;
const STEPS = [3, 7, 11]; // the reveal's three jumps (after the lamp's stutter), as the bull's and the lit knocks'
const TELL = 11; // the eyes tell at the final step, as he looks up over the bags
// the cone barely opens, as the bear's: his bags stay inside the light and the ink wedges stay
export const HOLDEN_FLARE = [0, 1, 2, 3];
// sheet palette index -> detail shade (visitors.js SHADE): the figure itself, pale cloth, lit from behind
const SHADE_OF = { 1: 0, 2: 2, 3: 3 };

const BASE = new URL('./art/aseprite/holden-door/', import.meta.url);
const isNode = !!globalThis.process?.versions?.node;
async function bytes(url) {
  if (isNode) { const fs = 'node:fs/promises'; return new Uint8Array(await (await import(fs)).readFile(url)); }
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return new Uint8Array(await r.arrayBuffer());
}
const text = async url => JSON.parse(new TextDecoder().decode(await bytes(url)));

// the sheet -> one mask per step, the same shape silhouetteMask (visitors.js) and beastMask (beasts.js) return
export function parseHolden(png, sheet, anchors) {
  const [MW, MH] = anchors.size;
  return sheet.frames.map(({ frame: f }, k) => {
    const m = new Uint8Array(MW * MH), d = new Uint8Array(MW * MH);
    let top = MH;
    for (let y = 0; y < MH; y++) for (let x = 0; x < MW; x++) {
      const i = png.index[(f.y + y) * png.w + f.x + x];
      if (!i || SHADE_OF[i] === undefined) continue;
      m[y * MW + x] = 1; d[y * MW + x] = SHADE_OF[i];
      if (y < top) top = y;
    }
    const a = anchors.steps[k];
    return { w: MW, h: MH, m, d, cx: anchors.cx, oy: anchors.oy, top, feet: anchors.feet,
      head: a.head, mouth: a.mouth, eyes: a.eyes, nostrils: [] };
  });
}
let masks = null;
try {
  const [png, sheet, anchors] = await Promise.all([bytes(new URL('holden-door-sheet.png', BASE)).then(decodeIndexed),
    text(new URL('holden-door-sheet.json', BASE)), text(new URL('holden-door-anchors.json', BASE))]);
  masks = parseHolden(png, sheet, anchors);
} catch (e) { console.warn('holden:', e.message); }
export const holdenReady = () => !!masks;

// The pose t seconds after the reveal; t < 0 (still knocking) is the same for every kind.
export function holdenPose(kind = null, t = -1) {
  const f = t == null || t < 0 ? -1 : Math.floor(t * FPS + 1e-6);
  const step = f < 0 ? 0 : STEPS.filter(s => f >= s).length;
  const eyes = f >= TELL && kind === 'alien' ? 'red' : f >= TELL && kind === 'infected' ? 'sick' : 'plain';
  return { step, eyes, k: 1 + 0.02 * step, up: 0 };
}
// the figure at a reveal step (0 knocking, 3 the full pose)
export const holdenMask = (step = 0) => masks?.[Math.max(0, Math.min(3, step))] ?? null;

// Everything drawGlass needs for one frame of his knock: beastState's fields (beasts.js), his own beam.
export function holdenState({ kind = null, revealT = -1, fr = 0, t = 0, outbreak = false, lamp = 1, approach = 1,
  jitter = 0, blink = false, eye = null, slip = false, redEyes = false } = {}) {
  const pose = holdenPose(kind, revealT);
  const M = holdenMask(pose.step);
  const base = 0.62 + 0.38 * approach;
  const s = base * pose.k;
  const sway = Math.sin(t * 1.3) * 0.8 + jitter;
  const gs = { lamp, fr, t, outbreak: !!outbreak, glitch: false, snow: 1, ground: false, glow: 0, sores: false, hidden: !M,
    M, sx: s, sy: s, ax: Math.round(GLASS.hx + sway), hv: GLASS.foot - (GLASS.foot - GLASS.hy) * base - pose.up, pres: 0.32 + 0.68 * approach,
    crisp: false, eyes: [], eyesOn: false, eyeTint: T.none, puffs: [], prints: [], puffTint: T.none,
    beam: { beast: 'holden', tint: T.holden, flare: HOLDEN_FLARE[pose.step] } };
  if (M) gs.eyes = M.eyes.map(([x, y]) => [Math.round(gs.ax + (x - M.cx) * s), Math.round(gs.hv + (y - M.oy) * s)]);
  gs.eyesOn = !!M && gs.pres > 0.85 && !blink;
  gs.eyeTint = pose.eyes === 'red' || redEyes ? T.red : pose.eyes === 'sick' || outbreak ? T.green : T.none;
  gs.eye = pickEye('S', eye, eyeLook(gs.eyeTint === T.red, pose.eyes === 'sick', kind, revealT), slip);
  return gs;
}
